// ---- Entity interpolation (shared) --------------------------------------------
// Delayed-bracket interpolation of a chronological transform buffer, shared by
// remote players, networked NPCs, and the fish-shadow mirror. Each renders at
// (now − INTERP_DELAY) by finding the two buffered samples bracketing that
// render time and lerping between them (net.ts owns the sample clock + delay via
// sampleRenderT). Pure + THREE-free, so a mirror that must NOT import net.ts
// (fish.ts) can still share the exact same scheme.
//
// updateRemotes keeps its OWN copy on purpose: it extrapolates past the newest
// sample along the last segment's velocity and carries moving/running flags —
// behaviour NPCs/fish don't want. Everything they DO share lives here.

/** A chronological transform sample. `t` is on net.ts's sample clock. */
export type InterpSnap = { t: number; x: number; y: number; z: number; ry: number };
/** The reusable scratch the sampler writes into (callers keep one, per frame). */
export type InterpOut = { x: number; y: number; z: number; ry: number };

/** Shortest-path angular lerp so a body turning past ±π doesn't spin the long way. */
export function lerpAngle(a: number, b: number, t: number): number {
  let d = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/** Prune `buf` to the interpolation window (MUTATES it — drops samples older than
 *  the render bracket so memory stays bounded), then write the sampled transform
 *  into `out`. Holds the oldest sample when history is short and the newest when
 *  we've run past it (no extrapolation — NPCs/fish freeze; remotes glide via
 *  their own copy). Returns false when the buffer is empty, so the caller can
 *  hold its last transform. */
export function sampleBuffer(buf: InterpSnap[], renderT: number, out: InterpOut): boolean {
  while (buf.length >= 2 && buf[1]!.t <= renderT) buf.shift();
  if (buf.length === 0) return false;
  if (renderT <= buf[0]!.t) {
    const s = buf[0]!;
    out.x = s.x; out.y = s.y; out.z = s.z; out.ry = s.ry;
    return true;
  }
  let a = buf[0]!, b = buf[buf.length - 1]!;
  for (let i = 0; i < buf.length - 1; i++) {
    if (buf[i]!.t <= renderT && renderT <= buf[i + 1]!.t) { a = buf[i]!; b = buf[i + 1]!; break; }
  }
  if (renderT >= b.t) {
    out.x = b.x; out.y = b.y; out.z = b.z; out.ry = b.ry;
    return true;
  }
  const span = b.t - a.t;
  const f = span > 0 ? (renderT - a.t) / span : 0;
  out.x = a.x + (b.x - a.x) * f;
  out.y = a.y + (b.y - a.y) * f;
  out.z = a.z + (b.z - a.z) * f;
  out.ry = lerpAngle(a.ry, b.ry, f);
  return true;
}
